import {
  DOCS_FORM_NAMES,
  GENDER,
  PREDICAMENT_STATE_NO,
  PREDICAMENT_STATE_YES,
  VISUALLY_IMPAIRED_DOCUMENT,
} from "core/consts";
import {
  ControlledCheckboxGroup,
  ControlledDateInput,
  ControlledInput,
  ControlledRadioGroup,
  ControlledTextarea,
} from "ds/ui";
import { FormFieldConfig } from "..";
import {
  ApplicationSubmittedApplicableField,
  BeforeRetirementPensionApplicableField,
  ClaimForDamagesApprovedField,
  ContributionsToForeignInsuranceInfoField,
  EmploymentStatusField,
  HealthDamagePresentField,
  InsuranceTypeField,
  MaritalStatusField,
  NeedsDisabilityEquipmentApplicableField,
  PatientUnableToWorkField,
  PriorEmploymentStatusField,
  RequestedRehabServiceField,
  RetirementPensionReceivedApplicableField,
  SendDocumentsPerEmailDescriptionField,
  SendDocumentsPerEmailField,
  ThirdPartyRequestField,
  UnemploymentBenefitField,
} from "./fields";

export const DRVGeneralFormSections: FormFieldConfig<
  typeof DOCS_FORM_NAMES.DRV_GENERAL
>["sections"] = [
  // Section Zero: Insurance Information
  {
    fields: {
      hospital_name_address: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.insuranceInformation
                .addressHospital
            }
            name="hospital_name_address"
            placeholder=" "
          />
        ),
        position: 4,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.insuranceInformation
              .subtitleHospital,
        },
      },
      insurance_company_name_address: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.insuranceInformation
                .addressHealthInsurance
            }
            name="insurance_company_name_address"
            placeholder=" "
          />
        ),
        position: 3,
      },
      pension_insurance_company_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.insuranceInformation
                .addressPensionInsurance
            }
            name="pension_insurance_company_name"
            placeholder=" "
          />
        ),
        position: 2,
      },
      preferred_rehab_clinic: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.insuranceInformation
                .informationAhbFacility
            }
            name="preferred_rehab_clinic"
            placeholder=" "
          />
        ),
        position: 6,
        subTitle: {
          className: "pb-3",
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.insuranceInformation
              .subtitleRehabClinic,
        },
      },
      social_worker_name_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.insuranceInformation
                .informationSocialWorker
            }
            name="social_worker_name_number"
            placeholder=" "
          />
        ),
        position: 5,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.insuranceInformation
              .subtitleSocialWorker,
        },
      },
      statutory_pension_insurance_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.insuranceInformation
                .pensionInsuranceNumber
            }
            name="statutory_pension_insurance_number"
            placeholder=" "
          />
        ),
        position: 1,
      },
    },
    position: 1,
    title: () => "",
  },
  // Section One: Requested Rehab Service
  {
    fields: {
      requested_rehab_service: {
        input: RequestedRehabServiceField,
        position: 1,
      },
    },
    position: 2,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionOne.title,
  },
  // Section Two: Patient Information
  {
    fields: {
      gender_extended: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.patientForms.drvGeneralForm.sectionTwo.male,
              value: GENDER.MALE,
            },
            {
              label: translations.patientForms.drvGeneralForm.sectionTwo.female,
              value: GENDER.FEMALE,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionTwo.diverse,
              value: GENDER.DIVERSE,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionTwo.unknown,
              value: GENDER.UNSPECIFIED,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={translations.patientForms.drvGeneralForm.sectionTwo.gender}
              name="gender_extended"
              options={options}
              size="sm"
            />
          );
        },
        position: 5,
      },
      patient_birth_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionTwo.maidenName
            }
            name="patient_birth_name"
            placeholder=" "
          />
        ),
        position: 3,
      },
      patient_birthdate: {
        input: ({ translations }) => (
          <ControlledDateInput
            label={
              translations.patientForms.drvMedicalForm.sectionZero
                .dateBirthPatient
            }
            name="patient_birthdate"
          />
        ),
        position: 6,
      },
      patient_city: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvGeneralForm.sectionTwo.city}
            name="patient_city"
            placeholder=" "
          />
        ),
        position: 9,
      },
      patient_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionTwo.firstName
            }
            name="patient_first_name"
            placeholder=" "
          />
        ),
        position: 1,
      },
      patient_last_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvGeneralForm.sectionTwo.lastName}
            name="patient_last_name"
            placeholder=" "
          />
        ),
        position: 2,
      },
      patient_maiden_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionTwo.formerNames
            }
            name="patient_maiden_name"
            placeholder=" "
          />
        ),
        position: 4,
      },
      patient_nationality: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionTwo.nationality
            }
            name="patient_nationality"
            placeholder=" "
          />
        ),
        position: 10,
      },
      patient_phone_number: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvGeneralForm.sectionTwo.phone}
            name="patient_phone_number"
            placeholder=" "
          />
        ),
        position: 7,
      },
      patient_street_housenumber: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvGeneralForm.sectionTwo.street}
            name="patient_street_housenumber"
            placeholder=" "
          />
        ),
        position: 8,
      },
      patient_zipcode: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvGeneralForm.sectionTwo.zipCode}
            name="patient_zipcode"
            placeholder=" "
          />
        ),
        position: 8,
      },
    },
    position: 3,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionTwo.title,
  },
  // Section Three: Care Person Necessary
  {
    fields: {
      care_person_necessary: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionThreeNew
                  .yesIncludeForm,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              className="w-full"
              label={
                translations.patientForms.drvGeneralForm.sectionThreeNew
                  .subtitle
              }
              name="care_person_necessary"
              options={options}
              size="sm"
            />
          );
        },
        position: 1,
      },
    },
    position: 4,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionThreeNew.title,
  },
  // Section Four: Marital Status and Employment
  {
    fields: {
      last_employment: {
        input: ({ translations }) => (
          <ControlledTextarea
            label={
              translations.patientForms.drvGeneralForm.sectionThree
                .fieldLabelProfession
            }
            name="last_employment"
            placeholder=" "
          />
        ),
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionThree
              .subtitleProfession,
        },
      },
      marital_status: {
        input: MaritalStatusField,
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionThree.subtitle,
        },
      },
    },
    position: 5,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionThree.title,
  },
  // Section Five: Employment Status
  {
    fields: {
      employment_status: {
        input: EmploymentStatusField,
        position: 1,
      },
    },
    position: 6,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionFour.title,
  },
  // Section Six: Prior Employment Status
  {
    fields: {
      prior_employment_status: {
        input: PriorEmploymentStatusField,
        position: 1,
      },
    },
    position: 7,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionFive.title,
  },
  // Section Seven: Insurance Information
  {
    fields: {
      insurance_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionSix.insuranceName
            }
            name="insurance_name"
            placeholder=" "
          />
        ),
        position: 1,
      },
      insurance_type: {
        input: InsuranceTypeField,
        position: 2,
      },
      patient_unable_to_work_before_hospitalization: {
        input: PatientUnableToWorkField,
        position: 3,
      },
    },
    position: 8,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionSix.insuranceInformation,
  },
  // Section Eight: Doctor Information
  {
    fields: {
      doctor_in_charge_in_hospital: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionSix.doctorName
            }
            name="doctor_in_charge_in_hospital"
            placeholder=" "
          />
        ),
        position: 1,
      },
      doctor_in_charge_in_hospital_first_name: {
        input: ({ translations }) => (
          <ControlledInput
            label={translations.patientForms.drvGeneralForm.doctorFirstName}
            name="doctor_in_charge_in_hospital_first_name"
            placeholder=" "
          />
        ),
        position: 2,
      },
      doctor_in_charge_in_hospital_phone: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionSeven.doctorPhone
            }
            name="doctor_in_charge_in_hospital_phone"
            placeholder=" "
          />
        ),
        position: 6,
      },
      postal_code_doctor: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionSeven
                .doctorZipCode
            }
            name="postal_code_doctor"
            placeholder=" "
          />
        ),
        position: 5,
      },
      street_house_number_doctor: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionSeven.doctorStreet
            }
            name="street_house_number_doctor"
            placeholder=" "
          />
        ),
        position: 3,
      },
      town_doctor: {
        input: ({ translations }) => (
          <ControlledInput
            label={
              translations.patientForms.drvGeneralForm.sectionSeven.doctorCity
            }
            name="town_doctor"
            placeholder=" "
          />
        ),
        position: 4,
      },
    },
    position: 9,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionSeven.title,
  },
  // Section Nine: Contributions
  {
    fields: {
      contributions_to_foreign_insurance_current: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              aria-label={
                translations.patientForms.drvGeneralForm.sectionEight
                  .currentContributionsAbroad
              }
              className="w-full"
              name="contributions_to_foreign_insurance_current"
              options={options}
              size="sm"
            />
          );
        },
        position: 3,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionEight
              .currentContributionsAbroad,
        },
      },
      contributions_to_foreign_insurance_info: {
        input: ContributionsToForeignInsuranceInfoField,
        position: 2,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionEight
              .contributionsAbroad,
        },
      },
      contributions_to_statutory_pension: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              aria-label={
                translations.patientForms.drvGeneralForm.sectionEight
                  .contributionsPension
              }
              className="w-full"
              name="contributions_to_statutory_pension"
              options={options}
              size="sm"
            />
          );
        },
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionEight
              .contributionsPension,
        },
      },
    },
    position: 10,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionEight.title,
  },
  // Section Ten: Unemployment Benefit
  {
    fields: {
      unemployment_benefit_type_2: {
        input: UnemploymentBenefitField,
        position: 1,
      },
    },
    position: 11,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionNine.title,
  },
  // Section Eleven: Pension and Health Damage Information
  {
    fields: {
      application_submitted_applicable: {
        input: ApplicationSubmittedApplicableField,
        position: 6,
      },
      before_retirement_pension_applicable: {
        input: BeforeRetirementPensionApplicableField,
        position: 3,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionTen
              .pensionOldAgeBenefits,
        },
      },
      claim_for_damages_approved: {
        input: ClaimForDamagesApprovedField,
        position: 8,
      },
      health_damage_present: {
        input: HealthDamagePresentField,
        position: 5,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionTen
              .workRelatedAccident,
        },
      },
      retirement_pension_received_applicable: {
        input: RetirementPensionReceivedApplicableField,
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionTen.pensionBenefits,
        },
      },
      risk_of_incapacity_caused_by_accident_third_person: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              aria-label={
                translations.patientForms.drvGeneralForm.sectionTen
                  .accidentOrPerson
              }
              className="w-full"
              name="risk_of_incapacity_caused_by_accident_third_person"
              options={options}
              size="sm"
            />
          );
        },
        position: 7,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionTen
              .accidentOrPerson,
        },
      },
      special_payments_from_civil_services: {
        input: ({ translations }) => {
          const options = [
            {
              label: translations.actions.no,
              value: PREDICAMENT_STATE_NO,
            },
            {
              label: translations.actions.yes,
              value: PREDICAMENT_STATE_YES,
            },
          ];

          return (
            <ControlledRadioGroup
              aria-label={
                translations.patientForms.drvGeneralForm.sectionTen
                  .pensionInformation
              }
              className="w-full"
              name="special_payments_from_civil_services"
              options={options}
              size="sm"
            />
          );
        },
        position: 0,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionTen
              .pensionInformation,
        },
      },
    },
    position: 12,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionTen.title,
  },
  // Section Twelve: Third Party Request
  {
    fields: {
      third_party_request: {
        input: ThirdPartyRequestField,
        position: 0,
      },
    },
    position: 13,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionEleven.title,
  },
  // Section Thirteen: Needs Disability Equipment
  {
    fields: {
      needs_disability_equipment_applicable: {
        input: NeedsDisabilityEquipmentApplicableField,
        position: 1,
      },
    },
    position: 14,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionTwelev.title,
  },
  // Section Fourteen: Send Documents
  {
    fields: {
      send_documents_per_email: {
        description: ({ translations }) =>
          translations.patientForms.drvGeneralForm.sectionThirteen.description,
        input: SendDocumentsPerEmailField,
        position: 1,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionThirteen
              .subtitleEmail,
        },
      },
      send_documents_per_email_description: {
        input: SendDocumentsPerEmailDescriptionField,
        position: 2,
      },
      visually_impaired_document: {
        input: ({ translations }) => {
          const options = [
            {
              label:
                translations.patientForms.drvGeneralForm.sectionThirteen
                  .largePrint,
              value: VISUALLY_IMPAIRED_DOCUMENT.LARGE_PRINT,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionThirteen
                  .brailleShort,
              value: VISUALLY_IMPAIRED_DOCUMENT.BRAILLE_SHORT,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionThirteen
                  .brailleFull,
              value: VISUALLY_IMPAIRED_DOCUMENT.BRAILLE_LONG,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionThirteen.cd,
              value: VISUALLY_IMPAIRED_DOCUMENT.CD,
            },
            {
              label:
                translations.patientForms.drvGeneralForm.sectionThirteen.audio,
              value: VISUALLY_IMPAIRED_DOCUMENT.AUDIO,
            },
          ];

          return (
            <ControlledCheckboxGroup
              label={
                translations.patientForms.drvGeneralForm.sectionThirteen
                  .additionalFormat
              }
              name="visually_impaired_document"
              options={options}
              size="sm"
            />
          );
        },
        position: 3,
        subTitle: {
          text: ({ translations }) =>
            translations.patientForms.drvGeneralForm.sectionThirteen
              .visuallyImpaired,
        },
      },
    },
    position: 15,
    title: ({ translations }) =>
      translations.patientForms.drvGeneralForm.sectionThirteen.title,
  },
];
